import { Helmet } from "react-helmet-async";
import { PageHero } from "@/components/shared/PageHero";
import { Users, Heart, TrendingUp, Shield, Briefcase, Clock, DollarSign, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";

const benefits = [
  {
    icon: TrendingUp,
    title: "Growth Opportunities",
    description: "We invest in our team's development with training and advancement paths.",
  },
  {
    icon: Users,
    title: "Team Environment",
    description: "Join a supportive crew that works together and celebrates wins together.",
  },
  {
    icon: Heart,
    title: "Work-Life Balance",
    description: "We respect your time and prioritize a healthy balance between work and family.",
  },
  {
    icon: Shield,
    title: "Safety First",
    description: "Your safety is our priority with proper equipment and training provided.",
  },
];

const jobOpenings = [
  {
    title: "Experienced Lawn Care Professional",
    type: "Full-Time",
    experience: "2+ years experience required",
    description: "We're looking for an experienced lawn care professional to join our team. You'll be responsible for operating commercial mowers, performing quality lawn maintenance, and mentoring junior team members.",
    responsibilities: [
      "Operate commercial mowing equipment safely and efficiently",
      "Perform trimming, edging, and blowing operations",
      "Apply fertilizers and weed control treatments",
      "Mentor and train new team members",
      "Maintain equipment and report any issues",
    ],
    requirements: [
      "2+ years of professional lawn care experience",
      "Valid driver's license with clean driving record",
      "Ability to lift 50+ lbs and work outdoors in various weather",
      "Strong work ethic and attention to detail",
      "Reliable transportation",
    ],
  },
  {
    title: "Lawn Care Associate",
    type: "Full-Time / Part-Time",
    experience: "No experience needed – we'll train you!",
    description: "Great opportunity to start a career in the green industry! No experience necessary – we provide all the training you need. Perfect for hardworking individuals who enjoy working outdoors.",
    responsibilities: [
      "Assist with lawn mowing and trimming operations",
      "Help with mulching and landscaping projects",
      "Load and unload equipment and materials",
      "Learn proper equipment operation and safety",
      "Provide excellent customer service",
    ],
    requirements: [
      "No prior experience required",
      "Must be 18 years or older",
      "Valid driver's license preferred",
      "Ability to work outdoors and lift 50+ lbs",
      "Positive attitude and willingness to learn",
      "Reliable and punctual",
    ],
  },
];

const Careers = () => {
  return (
    <>
      <Helmet>
        <title>Careers | Riekels Lawn Care & Landscaping</title>
        <meta
          name="description"
          content="Join the Riekels Lawn Care & Landscaping team. Explore career opportunities in lawn care and landscaping in the West Twin Cities Metro, MN."
        />
      </Helmet>
      <main>
        <PageHero
          badge="Join Our Team"
          title="Careers at Riekels"
          subtitle="Build your future with a company that values quality, integrity, and its people."
        />

        {/* About Working Here */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                What It's Like Working at Riekels
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                At Riekels Lawn Care & Landscaping, we're more than just a lawn care company—we're a 
                family. Our team takes pride in delivering exceptional service while supporting 
                each other every step of the way.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed">
                We believe in doing the job right, treating customers with respect, and creating a 
                workplace where everyone can thrive. We've built a reputation for quality that our 
                team members are proud to uphold.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="feature-card text-center">
                  <div className="icon-circle mx-auto mb-4">
                    <benefit.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-heading font-bold text-foreground mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Job Openings */}
        <section className="py-20 bg-section-alt">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <div className="icon-circle mx-auto mb-6">
                <Briefcase className="w-8 h-8" />
              </div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
                Current Openings
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                We're growing and looking for dedicated team members to join us!
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {jobOpenings.map((job, index) => (
                <div key={index} className="bg-card rounded-2xl border border-border p-8 flex flex-col">
                  <div className="mb-6">
                    <h3 className="text-2xl font-heading font-bold text-foreground mb-2">
                      {job.title}
                    </h3>
                    <div className="flex flex-wrap gap-3 mb-4">
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-secondary/10 text-secondary text-sm font-medium">
                        <Clock className="w-4 h-4" />
                        {job.type}
                      </span>
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-muted text-muted-foreground text-sm">
                        <Sun className="w-4 h-4" />
                        {job.experience}
                      </span>
                    </div>
                    <p className="text-muted-foreground">
                      {job.description}
                    </p>
                  </div>

                  <div className="space-y-4 flex-1">
                    <div>
                      <h4 className="font-heading font-semibold text-foreground mb-2">Responsibilities:</h4>
                      <ul className="space-y-1">
                        {job.responsibilities.map((item, idx) => (
                          <li key={idx} className="text-muted-foreground text-sm flex items-start gap-2">
                            <span className="text-secondary mt-1">•</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-heading font-semibold text-foreground mb-2">Requirements:</h4>
                      <ul className="space-y-1">
                        {job.requirements.map((item, idx) => (
                          <li key={idx} className="text-muted-foreground text-sm flex items-start gap-2">
                            <span className="text-secondary mt-1">•</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="mt-6 pt-6 border-t border-border">
                    <a href={`mailto:RiekelsLCL@gmail.com?subject=Application for: ${job.title}`}>
                      <Button className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground">
                        Apply Now
                      </Button>
                    </a>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <p className="text-muted-foreground">
              Don't see a position that fits?{" "}
                <a 
                  href="mailto:RiekelsLCL@gmail.com" 
                  className="text-secondary hover:underline font-medium"
                >
                  Send us your resume
                </a>{" "}
                and we'll keep you in mind for future opportunities.
              </p>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default Careers;
